/* Pinned feedback mode (W3-I) — the product feature (NOT the old review-tooling
 * "Send feedback" modal). Defaults OFF. When toggled on, the next click on any
 * element captures EXACTLY ONE pin — route, a stable locator, coordinates,
 * viewport, build, and a comment — then returns to navigation. The pin is
 * POSTed to the real /feedback endpoint (api/feedback.ts) with the additive
 * `pin` record.
 *
 * The pure geometry/locator lives in lib/pinLocator.ts (unit-tested). This
 * module owns the toggle, the one-shot click capture, the comment popover, and
 * the POST. Zero interference with normal navigation when off.
 *
 * HONEST SCOPE: there is no PNG storage wired (feedback is a jsonl + Supabase
 * mirror with no blob store), so a pin records everything EXCEPT the screenshot
 * and the comment box says so — no heavy screenshot pipeline is bolted on.
 */
import { html, render } from 'lit';
import { postFeedback } from '../api/feedback.ts';
import { AUTH } from '../lib/auth.ts';
import { BUILD_VERSION } from '../lib/version.ts';
import { elementLabel, pinGeometry, stableSelector } from '../lib/pinLocator.ts';
import type { FeedbackPin } from '../api/feedback.ts';

const PNG_NOTE = 'Screenshot capture isn’t wired yet — this pin records the element, its location, viewport and build with your note.';

let active = false;
/** true only between "mode on" and the one capturing click — the one-shot gate
 * so a second click never captures a second pin. */
let capturing = false;
let banner: HTMLElement | null = null;
let popover: HTMLElement | null = null;
let toggleBtn: HTMLButtonElement | null = null;

/** The client slug from a cockpit route, else null — attaches client context to
 * the pin's feedback record without guessing a brand name. */
function routeSlug(): string | null {
  const m = /^#\/client\/([^/]+)/.exec(location.hash);
  return m ? decodeURIComponent(m[1]!) : null;
}

/* ── click capture ──────────────────────────────────────────────────────── */

function onCapture(ev: MouseEvent): void {
  if (!capturing) return;
  const target = ev.target as Element | null;
  if (!target) return;
  // never pin our own chrome (the toggle, banner, or comment popover)
  if (target.closest('.fbpin-ui, #feedbackToggle')) return;
  ev.preventDefault();
  ev.stopPropagation();
  capturing = false;
  document.body.classList.remove('fbpin-capturing');
  if (banner) banner.remove(), (banner = null);
  capturePin(target, ev.clientX, ev.clientY);
}

function onKeydown(ev: KeyboardEvent): void {
  if (ev.key === 'Escape' && active) { ev.preventDefault(); exit(); }
}

/* ── the comment popover for one captured pin ───────────────────────────── */

/** The one pin currently being commented on (set at capture, cleared on exit).
 * Module-scoped so the send/re-render handlers are defined ONCE, not per open. */
let currentPin: FeedbackPin | null = null;

function capturePin(el: Element, clientX: number, clientY: number): void {
  const rect = el.getBoundingClientRect();
  const geo = pinGeometry(clientX, clientY, rect);
  const loc = stableSelector(el);
  currentPin = {
    route: location.hash || '#/clients',
    selector: loc.selector,
    exact: loc.exact,
    elementText: elementLabel(el),
    tag: el.tagName.toLowerCase(),
    x: geo.x, y: geo.y, relX: geo.relX, relY: geo.relY,
    rect: geo.rect,
    viewport: { w: window.innerWidth, h: window.innerHeight },
    build: BUILD_VERSION,
  };
  popover = document.createElement('div');
  popover.className = 'fbpin-ui fbpin-pop';
  renderPop('edit');
  document.body.appendChild(popover);
  positionPopover(currentPin.x, currentPin.y);
  (popover.querySelector('.fbpin-text') as HTMLTextAreaElement | null)?.focus();
}

async function sendPin(): Promise<void> {
  const pin = currentPin;
  if (!pin) return;
  const ta = popover?.querySelector('.fbpin-text') as HTMLTextAreaElement | null;
  const comment = (ta?.value ?? '').trim();
  if (!comment) { ta?.focus(); return; }
  renderPop('sending');
  try {
    await postFeedback({
      section: 'Pinned feedback',
      comment,
      author: AUTH.user?.name ?? '',
      brand: routeSlug(),
      page: location.pathname + location.search + location.hash,
      pin,
    });
    renderPop('sent');
    window.setTimeout(exit, 1100);
  } catch (err) {
    renderPop('error', (err as Error).message);
  }
}

type PopPhase = 'edit' | 'sending' | 'sent' | 'error';

function renderPop(phase: PopPhase, msg = ''): void {
  if (!popover || !currentPin) return;
  render(popTemplate(currentPin, phase, msg), popover);
}

function popTemplate(pin: FeedbackPin, phase: PopPhase, msg: string) {
  if (phase === 'sent') {
    return html`<div class="fbpin-head"><strong>Pinned — thanks</strong></div>
      <p class="fbpin-meta">Your pin is in the build backlog.</p>`;
  }
  const busy = phase === 'sending';
  const target = pin.elementText
    ? html`<code>${pin.tag}</code> · “${pin.elementText}”`
    : html`<code>${pin.tag}</code>`;
  return html`<div class="fbpin-head">
      <strong>Pin feedback</strong>
      <button class="fbpin-x" aria-label="Cancel" @click=${exit}>×</button>
    </div>
    <p class="fbpin-target">${target}</p>
    <p class="fbpin-meta">${pin.route} · ${pin.viewport.w}×${pin.viewport.h} · build ${pin.build}${pin.exact ? '' : ' · approx. locator'}</p>
    <textarea class="fbpin-text" rows="4" placeholder="What's wrong or missing here?" ?disabled=${busy}></textarea>
    <p class="fbpin-note">${PNG_NOTE}</p>
    ${phase === 'error' ? html`<p class="fbpin-err">Could not send: ${msg}. The service may be offline.</p>` : ''}
    <div class="fbpin-actions">
      <button class="fbpin-cancel" @click=${exit} ?disabled=${busy}>Cancel</button>
      <button class="fbpin-send" @click=${sendPin} ?disabled=${busy}>${busy ? 'Sending…' : 'Send pin'}</button>
    </div>`;
}

/** Clamp the popover into the viewport near the click point. Positioning is
 * inherently pixel-based (a click coordinate); rem tokens don't apply. */
function positionPopover(x: number, y: number): void {
  if (!popover) return;
  const w = popover.offsetWidth || 300;
  const h = popover.offsetHeight || 220;
  const pad = 16;
  const left = Math.max(pad, Math.min(x + 12, window.innerWidth - w - pad));
  const top = Math.max(pad, Math.min(y + 12, window.innerHeight - h - pad));
  popover.style.left = `${left}px`;
  popover.style.top = `${top}px`;
}

/* ── mode on/off ────────────────────────────────────────────────────────── */

function enter(): void {
  if (active) return;
  active = true;
  capturing = true;
  document.body.classList.add('fbpin-capturing');
  toggleBtn?.setAttribute('aria-checked', 'true');
  toggleBtn?.classList.add('on');
  banner = document.createElement('div');
  banner.className = 'fbpin-ui fbpin-banner';
  render(
    html`<span class="fbpin-dot" aria-hidden="true"></span>
      <span>Click anything to pin feedback</span>
      <kbd>esc</kbd><span>to exit</span>`,
    banner,
  );
  document.body.appendChild(banner);
  document.addEventListener('click', onCapture, { capture: true });
  document.addEventListener('keydown', onKeydown);
}

/** Fully return to navigation: tear down capture, banner, popover, listeners. */
export function exit(): void {
  if (!active) return;
  active = false;
  capturing = false;
  document.body.classList.remove('fbpin-capturing');
  toggleBtn?.setAttribute('aria-checked', 'false');
  toggleBtn?.classList.remove('on');
  document.removeEventListener('click', onCapture, { capture: true });
  document.removeEventListener('keydown', onKeydown);
  banner?.remove(); banner = null;
  popover?.remove(); popover = null;
  currentPin = null;
}

/** Wire the rail toggle. Off by default; clicking it enters pin mode, clicking
 * again (or Esc, or completing a pin) returns to navigation. */
export function initFeedbackPin(): void {
  toggleBtn = document.getElementById('feedbackToggle') as HTMLButtonElement | null;
  toggleBtn?.addEventListener('click', () => { active ? exit() : enter(); });
}

/** Test/inspection helper — whether pin mode is currently active. */
export function isPinActive(): boolean {
  return active;
}
